/**
 * Cloudflare Turnstile widget. Loads Cloudflare's own script on demand — the
 * one place this app fetches something off a CDN — because it only renders
 * inside FeedbackDialog, which already requires a connection. Nothing on the
 * path to a printed slate touches this file.
 *
 * SITE_KEY below is real, set from the Cloudflare dashboard (Turnstile is
 * free) — see the decisions log's "The contact form" section. The
 * REPLACE_WITH_ guard stays as a fallback: if this ever reverts to a
 * placeholder, the widget fails to render and FeedbackDialog shows
 * "Verification isn't ready yet" instead of quietly accepting anything.
 */

import { useEffect, useRef } from "react";

// Public by design — a site key identifies which site is asking, it does not
// authorise anything on its own. The secret half lives only as a Worker
// secret (TURNSTILE_SECRET), never in this bundle.
const SITE_KEY = "0x4AAAAAAEX7Z9KmsaliJ9v9";

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js";

interface TurnstileWindow extends Window {
  turnstile?: {
    render(
      container: HTMLElement,
      options: {
        sitekey: string;
        callback: (token: string) => void;
        "expired-callback"?: () => void;
        "error-callback"?: () => void;
      },
    ): string;
    reset(widgetId: string): void;
    remove(widgetId: string): void;
  };
}

let scriptPromise: Promise<void> | null = null;

function loadScript(): Promise<void> {
  scriptPromise ??= new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
      resolve();
      return;
    }
    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Turnstile script failed to load"));
    document.head.appendChild(script);
  });
  return scriptPromise;
}

interface TurnstileProps {
  onToken: (token: string | null) => void;
}

/** Renders nothing (and calls onToken(null)) until the placeholder SITE_KEY
 *  above is replaced — see the file header. */
export function Turnstile({ onToken }: TurnstileProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  // Read in a callback that fires long after render, via loadScript's promise
  // chain — a ref keeps it current without re-running the effect (and
  // re-rendering the widget) on every parent render.
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;

  useEffect(() => {
    if (SITE_KEY.startsWith("REPLACE_WITH_")) {
      onTokenRef.current(null);
      return;
    }

    let cancelled = false;

    void loadScript()
      .then(() => {
        if (cancelled || !containerRef.current) return;
        const w = window as TurnstileWindow;
        if (!w.turnstile) return;
        widgetIdRef.current = w.turnstile.render(containerRef.current, {
          sitekey: SITE_KEY,
          callback: (token) => onTokenRef.current(token),
          "expired-callback": () => onTokenRef.current(null),
          "error-callback": () => onTokenRef.current(null),
        });
      })
      .catch(() => onTokenRef.current(null));

    return () => {
      cancelled = true;
      const w = window as TurnstileWindow;
      if (widgetIdRef.current && w.turnstile) w.turnstile.remove(widgetIdRef.current);
    };
  }, []);

  return <div ref={containerRef} />;
}
